import React from "react";
import { isEmpty } from "lodash";
import { useTranslation } from "react-i18next";

import { PreviewLayout } from ".";

import { PreviewPersonalInformationBlock } from "@/components/PreviewPersonalInformationBlock";
import { PreviewEventDateBlock } from "@/components/PreviewEventDateBlock";
import { PreviewLocationBlock } from "@/components/PreviewLocationBlock";
import { PreviewHeadingBlock } from "@/components/PreviewHeadingBlock";
import { Typography } from "@/components/Typography";
import { IconList } from "@/components/IconList";
import { Link } from "@/components/Link";

import { PAGES } from "@/internals/constants/pages";

import { getFullAddress } from "@/utils/qrEditorHelper";
import { checkValidURL } from "@/utils/formHelper";

import { QREditorContext } from "@/contexts/QREditorContext";
import { TimezonesService } from "@/services/TimezonesService";
import { getLanguageCookie } from "@/services/LanguagePreferenceService";

import { QREditorContextProps, QRProps } from "@/contexts/QREditorContext/types";
import { TCalendarEvent } from "@/services/AddToCalendarService/types.ts";
import { LocationType } from "@/components/PreviewLocationBlock/types";
import { LabeledDataType } from "@/components/LabeledData/types";
import { QREventType } from "@/types/qr";
import { Icons } from "@/types/icons";

export default function PreviewEvent(props: QRProps<"event">) {
  const { t } = useTranslation(PAGES.QR_EVENT);
  const locale = getLanguageCookie();

  const qrEditorContextData = React.useContext<QREditorContextProps<"event">>(QREditorContext);
  const isInitPreview = props?.initPreview;

  const data = props?.data || qrEditorContextData?.qrData.data;

  const {
    eventDateTime,
    logoUrl,
    infoTitle,
    infoDescription,
    infoActionTitle,
    infoActionUrl,
    themeColor,
    facilities,
  } = data;

  const getTranslatedText = (translationKey: string) => t(translationKey);

  const getPreviewFormattedData = (data: QREventType) => {
    const {
      locationAddress,
      locationNumeration,
      locationPostalCode,
      locationCity,
      locationState,
      locationCountry,
      organizerName,
      organizerPhone,
      organizerEmail,
      organizerWebsite,
      organizerAbout,
    } = data;

    const addressData: LocationType = {
      address: locationAddress,
      numeration: locationNumeration,
      zipCode: locationPostalCode,
      city: locationCity,
      state: locationState,
      country: locationCountry,
      fullAddress: getFullAddress(
        locationAddress,
        locationNumeration,
        locationPostalCode,
        locationCity,
        locationState,
        locationCountry
      ),
    };

    const organizerData: LabeledDataType[] = [
      {
        label: getTranslatedText("preview_organizer_name"),
        value: organizerName,
      },
      {
        label: getTranslatedText("preview_organizer_phone"),
        value: organizerPhone,
      },
      {
        label: getTranslatedText("preview_organizer_email"),
        value: organizerEmail && (
          <Link
            externalLink
            className="business-link"
            href={`mailto:${organizerEmail}`}
            prefetch={false}
          >
            {organizerEmail}
          </Link>
        ),
      },
      {
        label: getTranslatedText("preview_organizer_website"),
        value: organizerWebsite && (
          <Link
            externalLink
            className="business-link"
            href={checkValidURL(organizerWebsite) || ""}
            prefetch={false}
          >
            {organizerWebsite}
          </Link>
        ),
      },
      {
        label: getTranslatedText("preview_organizer_about"),
        value: organizerAbout,
      },
    ];

    return {
      addressData,
      organizerData,
    };
  };

  const { addressData, organizerData } = getPreviewFormattedData(data);

  const calendarEvent: TCalendarEvent = {
    name: infoTitle || getTranslatedText("preview_event_name"),
    details: infoDescription || "",
    location: addressData?.fullAddress,
    startsAt: TimezonesService.convertDateFromTimezoneToLocalTimezone(
      eventDateTime?.start?.date,
      eventDateTime?.allDay ? "0:00" : eventDateTime?.start?.time,
      eventDateTime?.timezone
    ),
    endsAt: TimezonesService.convertDateFromTimezoneToLocalTimezone(
      eventDateTime?.end?.date,
      eventDateTime?.allDay ? "23:59" : eventDateTime?.end?.time,
      eventDateTime?.timezone
    ),
    timezone: eventDateTime?.timezone,
    allDay: eventDateTime?.allDay,
  };

  return (
    <PreviewLayout
      page="event"
      dataQA="preview-event-container"
      backgroundColor={themeColor?.backgroundColor}
      includeBrowser
      isInitPreview={isInitPreview}
    >
      <PreviewHeadingBlock
        isInitPreview={isInitPreview}
        image={logoUrl}
        title={infoTitle}
        text={infoDescription}
        actionText={infoActionTitle}
        actionUrl={infoActionUrl}
        actionColor={themeColor?.buttonColor}
      />

      <div className="qr-content">
        <PreviewEventDateBlock
          title={getTranslatedText("preview_time")}
          data={eventDateTime}
          labels={{
            start: getTranslatedText("preview_time_start"),
            end: getTranslatedText("preview_time_end"),
            schedule: getTranslatedText("preview_time_schedule"),
            allDay: getTranslatedText("preview_all_day"),
          }}
          calendarEvent={calendarEvent}
          themeColor={themeColor}
          locale={locale}
        />

        <PreviewLocationBlock
          title={getTranslatedText("preview_location")}
          data={addressData}
        />

        <PreviewPersonalInformationBlock
          title={getTranslatedText("preview_organizer")}
          iconName={Icons.users}
          data={organizerData}
        />

        {!isEmpty(facilities) && (
          <div className="u-card">
            <Typography
              Tag="p"
              size="m"
              classname="u-semibold"
            >
              {getTranslatedText("preview_facilities")}
            </Typography>
            <IconList
              compact
              items={facilities as unknown as string[]}
            />
          </div>
        )}
      </div>
    </PreviewLayout>
  );
}
